import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import IntroductionImg3 from "../../../assets/Images/Administrative Images/Infographics_Adjacencies.png";
import "../JaldiLab.css";
import {
  getTextContentWithHeading,
  referenceHtml,
  referenceTootKit,
  paraStyle,
  sectionStyle,
  h1style,
  h2style,
  h3style,
  h4style,
  getTextContentHeading,
  DividerBorderStyle,
  DashedBorderStyle,
  dimensionStyle,
  referenceStyle,
  captionWithBorders,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import { FigureDesc49 } from "../../../utils/tmp/ImageDescription/AdministrativeWingsID";

function AdministrativeWing({ goToSection }) {
  const referencesRef = useRef(null);
  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: "NCMS (2012). ",
    },
    {
      content: (
        <>
          Director General of Central Public Works Department,{" "}
          <i>A handbook of planning of office buildings (2013)</i>{" "}
        </>
      ),
      link: "https://cpwd.gov.in/publication/handbookofficebuilding.pdf",
      description:
        " <https://cpwd.gov.in/publication/handbookofficebuilding.pdf> pp. 29-30 [“Office Buildings Handbook”]",
    },
    {
      content: "Office Buildings Handbook, pp. 28-29. ",
    },
    {
      content: (
        <>
          Bureau of Indian Standards, ‘Requirements for Natural lighting and
          Ventilation’ in <i>National Building Code of India (2005)</i> pp.
          40-43.{" "}
        </>
      ),
    },
    {
      content: "Office Buildings Handbook, pp. 30-34. ",
    },
  ];

  const ObjectivesArea = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>Plan adequate space</span> within the
        Court Complex to accommodate all administrative offices, including
        maintenance and support facilities;
      </>,
      <>
        Ensure the{" "}
        <span style={{ color: "#DC002D" }}>
          smooth functioning of judicial processes
        </span>
        , by planning the layout of the administrative wing such that related
        functions are placed next to or in proximity to each other;
      </>,
      <>
        Design spaces for
        <span style={{ color: "#DC002D" }}>
          {" "}
          optimal use and to suit the specific functional requirements{" "}
        </span>{" "}
        of Court staff of each administrative section; and
      </>,
      <>
        Plan the layout of the office spaces to allow for a
        <span style={{ color: "#DC002D" }}>
          {" "}
          comfortable, engaging, and efficient working environment.{" "}
        </span>
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        The Administrative Wing must be close to the separate staff entry from
        the ground level (See{" "}
        <span
          onClick={() => {
            goToSection("/", "state-of");
          }}
          style={sectionStyle}
        >
          Section 1.2
        </span>
        ),and dedicated lifts and stairs,{reference(referenceData[0], 1)} along
        with an access control system as described in{" "}
        <span
          style={sectionStyle}
          onClick={() => {
            goToSection("annexes", "HeadingAnnexure3");
          }}
        >
          Annexure 3
        </span>
        .
      </>,
      "It may be located in a separate block connected to the Court Complex, or in a designated section or floor of the Court Complex.",
      <>
        There must be convenient and direct circulation between all
        administrative offices which are connected, a representative example of
        which is shown below in{" "}
        <span style={{ color: "#DC002D" }}>Figure 49</span>.
      </>,
      "Accessible designated washrooms, including family-friendly and gender-neutral washrooms, must be located within or close to the Administrative Wing.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const OfficeSpacesObject = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations for All Office Spaces",
    data: [
      "The required square footage for each of the Administrative office spaces must be determined based on the number of staff required to use that space, and the functions of each space. The space should be planned to prevent overcrowding so as to facilitate an unhindered workflow. ",

      "The layout must be planned to optimise the flow of people, documents, and information.",

      "Ramps, elevators, and other assistive devices must be installed to accommodate the needs of all staff and visitors. ",

      <>
        As detailed in{" "}
        <span
          style={sectionStyle}
          onClick={() => {
            goToSection("annexes", "HeadingAnnexure3");
          }}
        >
          Annexure 3
        </span>
        , robust security measures must be implemented to control access and
        maintain safety of Court staff, visitors, and sensitive information
        where required.
      </>,

      "In cases where certain administrative offices are required to have restricted access, such rooms must be access controlled with RFID/Access control systems, with a manual punch number override.",

      "Proper sound insulation must be implemented with the use of acoustically treated materials to minimise disturbances between adjacent spaces.",

      <>
        Special consideration must be given to noise control in open office
        settings, with absorptive finish materials and sufficient separation
        between individual occupants.{reference(referenceData[1], 2)}{" "}
        Acoustically absorptive workstation screens must be provided to ensure
        additional privacy.
      </>,

      "All noise sources (eg: ventilation systems, office equipment, and water coolers) must not exceed 40 dBA.",

      // <>
      //   Carpet flooring may be used to avoid stepping noise and unwanted
      //   reflection of sound. For further details, please refer to{" "}
      //   <span
      //     onClick={() => {
      //       goToSection("annexes", "HeadingAnnexure5");
      //     }}
      //     style={sectionStyle}
      //   >
      //     Annexure 5
      //   </span>
      //   .
      // </>,

      "Court management systems, audio-visual equipment, digital document storage, and IT infrastructure (including Internet access, Local Area Networks (LAN)/Wide Area Networks (WAN)/Wireless Fidelity (Wi-Fi) and other voice, cable or data systems) must be provided throughout the administrative wing.",
      <>
        Where necessary, floors may be raised to allow for easy access to
        cabling and power distribution, with due consideration for levelled
        flooring, with a threshold not more than{" "}
        <span style={dimensionStyle}>20 mm</span>.
      </>,

      <>
        Features such as plug-and-play floor boxes for power, data, voice and
        fibre, modular and harnessed wiring and buses, and conferencing hubs may
        be considered to allow for daily flexibility at work, as well as future
        reorganisation of office workstations.{reference(referenceData[2], 3)}
      </>,

      "Ergonomic and functional furniture must be provided, with comfortable seating arrangements.",

      "Ample storage space must be provided for official files (compact storage may be used where necessary) and other documents as well as personal belongings of Court staff.",
      <>
        Natural light must be maximised to ensure that the health and
        psychological well-being of the Court staff is maintained. A minimum of
        100-150 LUX of diffused indirect natural light is desirable for each{" "}
        space.{reference(referenceData[3], 4)}
      </>,

      "For the interiors of each space, materials/paint with light-colour shades may be preferred, to minimise heat gain and control glare.",

      <>
        Fine window screens that reduce illumination and glare while allowing
        contact with the view, or screens or louvers that operate upward from
        the window sill, may be used.{reference(referenceData[4], 5)}
      </>,
    ],

    isOrdered: false,
    isImage: true,
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="headingWaitingAreas" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.5. </span>
          Administrative Wing
        </h3>
        <p style={paraStyle}>
          Court staff comprise an integral part of the judicial system. Various
          administrative functions are required to ensure that the system runs
          smoothly. A separate administrative wing is therefore required to
          house, inter alia, the Registrars and other administrative officers,
          officials working in the accounts section, computer section, copying
          section, process server section, vigilance officers, etc.
        </p>
        <p style={paraStyle}>
          Some of these administrative offices have public-facing functions. For
          example, the filing section and pending branch of the Courtrooms must
          be open for the use of Advocates and litigants, in addition to Court
          staff assigned to these sections. On the other hand, some
          administrative offices are private. For example, the Record Room may
          only be accessed with the relevant permissions having been obtained.
        </p>
        {getTextContentHeading(ObjectivesArea)}
        {getTextContentHeading(adjacenciesObject)}
        <CaptionWithBorders1
          image={[IntroductionImg3]}
          caption={
            "Figure 49: Representative example of adjacencies between offices in the Administrative Wing"
          }
          isZoom={true}
          zoomImgStyle={{ height: "500px", objectFit: "contain" }}
          imageDescription={FigureDesc49}
        />
        {getTextContentHeading(OfficeSpacesObject)}
        <h2 ref={referencesRef} id="reference" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
}

export default AdministrativeWing;
